import {
  buildAuthoringPlan,
  buildRenderPlan,
  collectRegionBorders,
  dedupeBorderSegments,
  flattenZones,
  splitChildBoxes,
  type BorderSegment,
  type RenderPlan,
} from '@tsagko/label-zpl-pipeline'
import { getPrinterProfile, getPrinterProfiles } from '@tsagko/label-printer-profiles'
import type {
  PrinterProfile,
  ResolvedImage,
  Template,
  VariableMeta,
  Zone,
} from '@tsagko/label-contracts'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { clampSplitRatio, ratioForChildSize } from '../block-configurator/dimension-ops'
import { computeZoneLayout, type LaidOutZone } from './layout'
import { RenderPlanItem } from './RenderPlanItem'
import type { SplitDirection } from './zone-ops'
import { ZoneRenderer } from './ZoneRenderer'

// Divider drag affordances (issue #126). The hit box is intentionally wider
// than the visible line — grabbing a 4px-wide strip was near-impossible —
// while the painted highlight (Layer 1.5's inner child) stays thin.
const DIVIDER_HIT_PX = 12
const DIVIDER_VISUAL_PX = 4
// Offset (px) of the delete × along the divider, away from dead-center, so it
// no longer sits exactly where a drag gesture starts.
const DIVIDER_MERGE_BUTTON_OFFSET_PX = 10

interface SplitInfo {
  path: string[]
  direction: 'row' | 'column'
  ratio: number
  box: { x: number; y: number; w: number; h: number }
}

function collectSplits(
  zone: Zone,
  path: string[],
  box: { x: number; y: number; w: number; h: number }
): SplitInfo[] {
  if (!zone.split || !zone.children) return []
  const out: SplitInfo[] = [{
    path,
    direction: zone.split.direction,
    ratio: zone.split.ratio,
    box,
  }]
  const [boxA, boxB] = splitChildBoxes(box, zone.split)
  out.push(...collectSplits(zone.children[0], [...path, '0'], boxA))
  out.push(...collectSplits(zone.children[1], [...path, '1'], boxB))
  return out
}

interface CanvasProps {
  template: Template
  pxPerMm?: number
  selectedZoneId?: string
  proppedZoneIds?: Set<string>
  pickMode?: boolean
  /**
   * Sample / live data record. `locale` (prop) takes precedence over
   * `data._locale` or `data.locale` when picking a localized variant.
   */
  data?: Record<string, unknown>
  locale?: string
  profile?: PrinterProfile
  /**
   * Which layout planner to use. Default 'print' preserves today's behavior
   * (used by PreviewPanel's sample-data tier-1 preview). TemplateDesignerRoute
   * passes 'authoring' for the main editing canvas so bound primitives without
   * data show as {fieldKey} placeholders.
   */
  mode?: 'authoring' | 'print'
  onSelect?: (zone: LaidOutZone) => void
  onSplit?: (zone: LaidOutZone, direction: SplitDirection) => void
  /** Called when a drag-drop lands on a leaf zone. `payload` is the raw dataTransfer string. */
  onDrop?: (payload: string, zone: LaidOutZone) => void
  /**
   * When provided, divider overlays are rendered on internal splits and drag
   * events call this handler with the new ratio. When undefined, no dividers
   * are rendered.
   */
  onSplitRatioChange?: (path: string[], ratio: number) => void
  /**
   * Called when a divider drag ends (pointerup). Useful for coalescing all
   * the intermediate ratio changes into a single undo history entry.
   */
  onSplitRatioDragEnd?: (path: string[]) => void
  /**
   * When provided, a small × overlay is rendered on each divider that, when
   * clicked, requests a merge of the split at that path. The host wires up
   * the actual merge decision tree (silent vs. confirm).
   */
  onMergeAtPath?: (path: string[]) => void
  /**
   * Variable metadata map for the block being edited (Task 13 of the
   * block-variables-and-region-table slice). Threaded through to
   * `RenderPlanItem` for future WYSIWYG resolution via `resolveContent`.
   * Not consumed by the canvas itself yet — the plan-builder pre-resolves
   * text into `plan.text` until Task 14 lands. Optional and forward-compatible.
   */
  variables?: Record<string, VariableMeta>
  /**
   * Resolved + rasterized images keyed by component id, for WYSIWYG image display.
   * When provided, the plan builder will embed the 1-bit buffer into each image plan
   * so the authoring canvas shows the actual printed pixels.
   */
  resolvedImages?: Map<string, ResolvedImage>
}

// Stable empty-data sentinel so callers that omit `data` don't blow the
// `useMemo` cache for `plans` on every parent re-render.
const EMPTY_DATA: Record<string, unknown> = Object.freeze({})

function defaultProfile(): PrinterProfile {
  const all = getPrinterProfiles()
  if (all.length === 0) {
    throw new Error('Canvas: no printer profiles registered')
  }
  return getPrinterProfile('zebra-zd220-203') ?? all[0]!
}

export function Canvas({
  template,
  pxPerMm = 4,
  selectedZoneId,
  proppedZoneIds,
  pickMode,
  data = EMPTY_DATA,
  locale = 'en',
  profile,
  mode = 'print',
  onSelect,
  onSplit,
  onDrop,
  onSplitRatioChange,
  onSplitRatioDragEnd,
  onMergeAtPath,
  variables,
  resolvedImages,
}: CanvasProps) {
  // UI chrome only — `locale` above is the LABEL's data locale (date/number
  // rendering inside the artwork), which is independent of the app language.
  const { t } = useTranslation()
  const zones = useMemo(
    () =>
      computeZoneLayout(template.rootZone, {
        widthMm: template.widthMm,
        heightMm: template.heightMm,
      }),
    [template]
  )

  // All splits in the tree, collected so the Layer 1.5 ratio-drag / merge-X
  // overlay can reuse the list (gated on `onSplitRatioChange || onMergeAtPath`).
  const splits = useMemo(
    () => collectSplits(template.rootZone, [], { x: 0, y: 0, w: template.widthMm, h: template.heightMm }),
    [template]
  )

  const borderSegments = useMemo<BorderSegment[]>(
    () =>
      dedupeBorderSegments(
        collectRegionBorders(
          zones.map(z => ({ xMm: z.x, yMm: z.y, widthMm: z.w, heightMm: z.h, borders: z.borders }))
        )
      ),
    [zones]
  )

  const resolvedProfile = profile ?? defaultProfile()

  const plans: RenderPlan[] = useMemo(() => {
    const dataWithLocale = { ...data, _locale: locale }
    const builder = mode === 'authoring' ? buildAuthoringPlan : buildRenderPlan
    const { plans } = builder(
      flattenZones(template),
      dataWithLocale,
      resolvedProfile,
      resolvedImages ?? new Map(),
      variables
    )
    return plans
  }, [template, data, locale, resolvedProfile, mode, variables, resolvedImages])

  return (
    <div
      data-testid="canvas-surface"
      className="relative bg-white shadow-sm ring-1 ring-stone-200 dark:bg-stone-100 dark:ring-stone-800"
      style={{
        width: template.widthMm * pxPerMm,
        height: template.heightMm * pxPerMm,
      }}
    >
      {/* Layer 1: zone interaction (borders, click, split buttons). */}
      {zones.map(z => (
        <ZoneRenderer
          // Key by path-qualified id: a baked-block-instance's expansion zone
          // tree reuses whatever ids the block's own layout defines (e.g. a
          // conventional `expansion-root`), so two independent instances on
          // the same canvas can legitimately produce the same `z.id`. `z.path`
          // encodes the exact tree location (split branches + baked-instance
          // ids) and is always unique, so pairing it with the id disambiguates
          // without touching persisted zone ids.
          key={`${z.path.join('.')}:${z.id}`}
          zone={z}
          pxPerMm={pxPerMm}
          isSelected={z.id === selectedZoneId}
          isPropped={proppedZoneIds?.has(z.id)}
          pickMode={pickMode}
          onSelect={onSelect}
          onSplit={onSplit}
          onDrop={onDrop}
        />
      ))}
      {/* Layer 1.25: region border lines (clipped to each cell, de-duped).
          Pure visuals — no pointer events — under the drag overlay below. */}
      {borderSegments.map((s, idx) => {
        const isVertical = s.axis === 'vertical'
        const thicknessPx = Math.max(1, s.thicknessMm * pxPerMm)
        const lengthPx = s.lengthMm * pxPerMm
        const leftPx = isVertical ? s.xMm * pxPerMm - thicknessPx / 2 : s.xMm * pxPerMm
        const topPx = isVertical ? s.yMm * pxPerMm : s.yMm * pxPerMm - thicknessPx / 2
        const widthPx = isVertical ? thicknessPx : lengthPx
        const heightPx = isVertical ? lengthPx : thicknessPx
        let background: string
        if (s.style === 'solid') {
          background = 'black'
        } else {
          const dash = s.style === 'dashed' ? '4px' : '1px'
          const gap = '2px'
          const angle = isVertical ? '180deg' : '90deg'
          background = `repeating-linear-gradient(${angle}, black 0, black ${dash}, transparent ${dash}, transparent calc(${dash} + ${gap}))`
        }
        return (
          <div
            key={`border-line-${idx}`}
            data-testid="zone-border"
            data-border-axis={s.axis}
            data-border-style={s.style}
            aria-hidden
            style={{
              position: 'absolute',
              left: leftPx,
              top: topPx,
              width: widthPx,
              height: heightPx,
              pointerEvents: 'none',
              background,
            }}
          />
        )
      })}
      {/* Layer 1.5: split-divider drag handles (edit mode when either ratio-drag or merge is provided). */}
      {(onSplitRatioChange || onMergeAtPath) && splits.map(s => {
        const isRow = s.direction === 'row'
        // Divider position in mm: at box.x + box.w*ratio (row) or box.y + box.h*ratio (column).
        const dividerLeftMm = isRow ? s.box.x + s.box.w * s.ratio : s.box.x
        const dividerTopMm = isRow ? s.box.y : s.box.y + s.box.h * s.ratio
        // The drag HIT-BOX (DIVIDER_HIT_PX) is wider than the visible line
        // (DIVIDER_VISUAL_PX) — issue #126: a hit target exactly as wide as the
        // 4px line was near-impossible to grab. The hit box is invisible; a
        // centered inner child paints the thin highlight so the visual stays
        // unchanged.
        const widthPx = isRow ? DIVIDER_HIT_PX : s.box.w * pxPerMm
        const heightPx = isRow ? s.box.h * pxPerMm : DIVIDER_HIT_PX
        const leftPx = isRow ? dividerLeftMm * pxPerMm - DIVIDER_HIT_PX / 2 : dividerLeftMm * pxPerMm
        const topPx = isRow ? dividerTopMm * pxPerMm : dividerTopMm * pxPerMm - DIVIDER_HIT_PX / 2
        const pathKey = s.path.length === 0 ? 'root' : s.path.join('-')
        const visualInset = (DIVIDER_HIT_PX - DIVIDER_VISUAL_PX) / 2
        return (
          <div
            key={`divider-${pathKey}`}
            data-testid={`divider-${pathKey}`}
            className="group absolute z-20"
            style={{
              position: 'absolute',
              left: leftPx,
              top: topPx,
              width: widthPx,
              height: heightPx,
              cursor: onSplitRatioChange ? (isRow ? 'col-resize' : 'row-resize') : 'default',
            }}
            onPointerDown={onSplitRatioChange ? (e => {
              e.stopPropagation()
              if (e.currentTarget.setPointerCapture) {
                e.currentTarget.setPointerCapture(e.pointerId)
              }
            }) : undefined}
            onPointerMove={onSplitRatioChange ? (e => {
              if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
              const surface = e.currentTarget.parentElement
              if (!surface) return
              const rect = surface.getBoundingClientRect()
              const pointerMmAxis = isRow
                ? (e.clientX - rect.left) / pxPerMm - s.box.x
                : (e.clientY - rect.top) / pxPerMm - s.box.y
              const totalMm = isRow ? s.box.w : s.box.h
              const ratio = ratioForChildSize(pointerMmAxis, totalMm)
              // ratio is pre-clamped here via clampSplitRatio; handler-side
              // clampSplitRatioWithFloor (TemplateDesignerRoute) will clamp again — harmless.
              onSplitRatioChange(s.path, clampSplitRatio(ratio))
            }) : undefined}
            onPointerUp={onSplitRatioChange ? (e => {
              if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId)
              }
              onSplitRatioDragEnd?.(s.path)
            }) : undefined}
          >
            {/* Visual-only thin highlight, centered in the wider invisible hit box. */}
            <div
              data-testid={`divider-visual-${pathKey}`}
              aria-hidden
              className="pointer-events-none absolute bg-sky-400/0 group-hover:bg-sky-400/30"
              style={
                isRow
                  ? { left: visualInset, top: 0, width: DIVIDER_VISUAL_PX, height: '100%' }
                  : { left: 0, top: visualInset, width: '100%', height: DIVIDER_VISUAL_PX }
              }
            />
            {onMergeAtPath && (
              <button
                type="button"
                data-testid={`divider-merge-${pathKey}`}
                aria-label={t('canvas.divider.delete')}
                onPointerDown={e => e.stopPropagation()}
                onClick={e => {
                  e.stopPropagation()
                  onMergeAtPath(s.path)
                }}
                // Off the drag axis (issue #126): centered on the CROSS axis,
                // offset toward one end along the divider itself, so it no
                // longer competes pixel-for-pixel with the drag gesture.
                // Opacity (not `hidden`/display:none) keeps it in the a11y
                // tree so Tab can reach it; visible on hover OR focus.
                style={
                  isRow
                    ? { left: '50%', top: DIVIDER_MERGE_BUTTON_OFFSET_PX }
                    : { left: DIVIDER_MERGE_BUTTON_OFFSET_PX, top: '50%' }
                }
                className={[
                  'pointer-events-auto absolute flex h-7 w-7 items-center justify-center rounded-full',
                  'bg-rose-600 text-xs font-bold text-white shadow opacity-0 transition',
                  isRow ? '-translate-x-1/2' : '-translate-y-1/2',
                  'group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 hover:bg-rose-700',
                ].join(' ')}
              >
                ×
              </button>
            )}
          </div>
        )
      })}
      {/* Layer 2: plan-driven content overlay (ZPL-truthful anchors). */}
      <div
        data-testid="render-plan-overlay"
        className="pointer-events-none absolute inset-0 text-stone-900 dark:text-stone-900"
      >
        {plans.map((plan, idx) => (
          <RenderPlanItem
            // Key by index-scoped id: when the same block is placed twice
            // (e.g. two instances of qa-matrix-block) their internal component
            // ids are identical, causing React duplicate-key errors. Appending
            // the list index disambiguates without changing plan.id (which
            // tracks data-render-plan-id for parity tests and must stay stable).
            key={`${plan.id}-${idx}`}
            plan={plan}
            pxPerMm={pxPerMm}
            variables={variables}
            data={data}
          />
        ))}
      </div>
    </div>
  )
}
